import {
  ChakraProvider,
} from '@chakra-ui/react';
import PropTypes from 'prop-types';
import AuthLanding from '../../../components/Auth/AuthLanding';
import FreeTrialForm from '../../../components/Auth/FreeTrial/FreeTrialForm';
import { freeTrialAuthReturnTo } from '../../../libs/auth/freeTrialOffice';
import theme from '../../../styles/theme';
import auth0 from '../../../utils/auth0';

const INCLUDED = [
  'Full access to Appraisers Now for 30 days',
  'Order management and scheduling tools',
  'Onboarding support and an Account Manager',
  'No credit card required',
];

const PLAN_HIGHLIGHTS = [
  'Order management and scheduling, built for appraisers',
  'Onboarding support to get you up and running',
  'Secure checkout. Cancel anytime.',
];

export default function FreeTrialPage({
  auth0Browser,
  referral,
  requestedPlan,
}) {
  const hasPlan = typeof requestedPlan === 'string';

  return (
    <ChakraProvider theme={theme}>
      <AuthLanding
        title="Create your account"
        subtitle={hasPlan
          ? 'Step 1 of 3. Takes about a minute.'
          : 'One form. No credit card. 30 days free.'}
        brandEyebrow={hasPlan ? '' : 'Free trial'}
        brandHeading={hasPlan ? 'Let’s get you set up' : 'Start your 30 day free trial'}
        brandDescription={hasPlan
          ? 'Three quick steps and you’re running orders.'
          : 'Create your account and company in one step. You will be ready to run orders as soon as you submit.'}
        brandHighlightsLabel={hasPlan ? null : "What's included"}
        brandHighlights={hasPlan ? PLAN_HIGHLIGHTS : INCLUDED}
      >
        <FreeTrialForm
          auth0Browser={auth0Browser}
          referral={referral}
          requestedPlan={requestedPlan}
        />
      </AuthLanding>
    </ChakraProvider>
  );
}

FreeTrialPage.propTypes = {
  auth0Browser: PropTypes.shape({
    audience: PropTypes.string,
    clientId: PropTypes.string,
    connection: PropTypes.string,
    domain: PropTypes.string,
    redirectUri: PropTypes.string,
  }),
  referral: PropTypes.string,
  requestedPlan: PropTypes.string,
};

FreeTrialPage.defaultProps = {
  auth0Browser: null,
  referral: null,
  requestedPlan: null,
};

FreeTrialPage.publicPage = true;

export async function getServerSideProps({ req, res, query }) {
  const session = await auth0.getSession(req, res);
  if (session) {
    return {
      redirect: {
        destination: freeTrialAuthReturnTo(
          typeof query.plan === 'string' ? query.plan : null,
        ),
        permanent: false,
      },
    };
  }

  const referral = typeof query.ref === 'string' ? query.ref : null;
  const requestedPlan = typeof query.plan === 'string' ? query.plan : null;
  const baseUrl = String(process.env.REDIRECT_URI || '').replace(/\/$/, '');

  return {
    props: {
      auth0Browser: {
        audience: process.env.AUDIENCE || '',
        clientId: process.env.AUTH0_CLIENT_ID || '',
        connection: process.env.AUTH0_CONNECTION || 'Username-Password-Authentication',
        domain: String(process.env.AUTH0_ISSUER_BASE_URL || '')
          .replace(/^https?:\/\//, '')
          .replace(/\/$/, ''),
        redirectUri: baseUrl
          ? `${baseUrl}/auth/free-trial/cross-origin-callback`
          : '',
      },
      referral,
      requestedPlan,
    },
  };
}
